import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageShell from '@/components/Layout/PageShell';
import { PageHeader } from '@/components/UI/PageHeader';
import { Tabs } from '@/components/UI/Tabs';
import { Button } from '@/components/UI/Button';
import SearchableSelect from '@/components/UI/SearchableSelect';
import { fieldClass } from '@/components/UI/fieldStyles';
import { cn } from '@/utils/cn';
import { useListPlatformTenantsQuery } from '@/redux/internal-services/platformAdminApi';
import AuditLogTab from '@/components/PlatformLogs/AuditLogTab';
import ActivityLogTab from '@/components/PlatformLogs/ActivityLogTab';
import AppLogTab from '@/components/PlatformLogs/AppLogTab';
import TraceTab from '@/components/PlatformLogs/TraceTab';

type LogTab = 'audit' | 'activity' | 'app' | 'trace';

const TABS: { id: LogTab; label: string }[] = [
  { id: 'audit', label: 'Audit' },
  { id: 'activity', label: 'Activity' },
  { id: 'app', label: 'Application' },
  { id: 'trace', label: 'Trace' },
];

/**
 * Platform admin Logs page. Tab, tenant, date range and trace id live in the URL so a filtered
 * view or a trace can be shared as a link.
 */
export default function LogsPage() {
  const [params, setParams] = useSearchParams();
  const tab = (TABS.some((t) => t.id === params.get('tab')) ? params.get('tab') : 'audit') as LogTab;
  const tenantId = params.get('tenant') ?? '';
  const fromDate = params.get('from') ?? '';
  const toDate = params.get('to') ?? '';
  const traceId = params.get('cid') ?? '';

  const update = (patch: Record<string, string>) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
        return next;
      },
      { replace: true },
    );

  const openTrace = (cid: string) =>
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', 'trace');
      next.set('cid', cid);
      return next;
    });

  const { data: tenantsData } = useListPlatformTenantsQuery({ limit: 200 });
  const tenantOptions = useMemo(
    () =>
      (tenantsData?.data ?? []).map((t) => ({ value: t.tenant_id, label: t.company_name, description: t.tenant_id })),
    [tenantsData],
  );

  const shared = { tenantId, fromDate, toDate, onTrace: openTrace };
  const hasSharedFilters = Boolean(tenantId || fromDate || toDate);

  return (
    <PageShell
      maxWidth="max-w-7xl"
      header={
        <PageHeader
          title="Logs"
          subtitle="Audit trail, business activity and application logs across all tenants. Click any trace ID to follow one request end to end."
          toolbar={
            <div className="flex flex-col gap-3">
              {tab !== 'trace' && (
                <div className="flex flex-wrap items-center gap-3">
                  <SearchableSelect
                    value={tenantId || null}
                    options={tenantOptions}
                    onChange={(v) => update({ tenant: v ?? '' })}
                    placeholder="All tenants"
                    ariaLabel="Filter by tenant"
                    allowClear
                    className="w-full sm:w-64"
                  />
                  <label className="flex items-center gap-2 text-xs text-ui-text2">
                    From
                    <input type="datetime-local" aria-label="From date" value={fromDate}
                      onChange={(e) => update({ from: e.target.value })} className={cn(fieldClass({ size: 'sm' }), 'w-auto')} />
                  </label>
                  <label className="flex items-center gap-2 text-xs text-ui-text2">
                    To
                    <input type="datetime-local" aria-label="To date" value={toDate}
                      onChange={(e) => update({ to: e.target.value })} className={cn(fieldClass({ size: 'sm' }), 'w-auto')} />
                  </label>
                  {hasSharedFilters && (
                    <Button variant="ghost" size="sm" onClick={() => update({ tenant: '', from: '', to: '' })}>
                      Clear
                    </Button>
                  )}
                </div>
              )}
              <Tabs tabs={TABS} value={tab} onChange={(id) => update({ tab: id })} label="Log type" />
            </div>
          }
        />
      }
    >
      {tab === 'audit' && <AuditLogTab {...shared} />}
      {tab === 'activity' && <ActivityLogTab {...shared} />}
      {tab === 'app' && <AppLogTab {...shared} />}
      {tab === 'trace' && <TraceTab correlationId={traceId} onChange={(cid) => update({ cid })} />}
    </PageShell>
  );
}
